"use client"

import { authIdentity } from "@defuse-protocol/internal-utils"
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/16/solid"
import Button from "@src/components/Button"
import { Copy } from "@src/components/CopyButton"
import { midTruncate } from "@src/components/DefuseSDK/features/withdraw/components/WithdrawForm/utils"
import { useConnectWallet } from "@src/hooks/useConnectWallet"
import { logger } from "@src/utils/logger"
import { QRCodeSVG } from "qrcode.react"
import { useCallback, useState } from "react"

const InternalAddressView = () => {
  const [shareCopied, setShareCopied] = useState(false)
  const { state } = useConnectWallet()

  const internalAddress =
    state.address != null && state.chainType != null
      ? authIdentity.authHandleToIntentsUserId(state.address, state.chainType)
      : undefined

  const handleShareAddress = useCallback(async () => {
    if (!internalAddress) return

    const shareUrl = `${window.location.origin}/contacts?${new URLSearchParams({ address: internalAddress })}`

    if (navigator.share) {
      try {
        await navigator.share({ url: shareUrl })
      } catch (err) {
        logger.error(err)
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl)
        setShareCopied(true)
        setTimeout(() => setShareCopied(false), 2000)
      } catch (err) {
        logger.error(err)
      }
    }
  }, [internalAddress])

  if (!internalAddress) return <div>No internal address.</div>

  return (
    <div className="space-y-4">
      <div className="dark:-outline-offset-1 space-y-2 rounded-3xl bg-gray-900 p-4 sm:p-5 dark:bg-gray-950 dark:outline dark:outline-white/5">
        <div className="flex items-center justify-center rounded-xl bg-green-500 px-4 py-4 sm:py-8">
          <div className="flex size-48 items-center justify-center rounded-3xl border-5 border-gray-900 bg-white">
            <QRCodeSVG
              value={internalAddress}
              size={160}
              fgColor="#171717"
              className="size-40"
            />
          </div>
        </div>

        <div className="rounded-xl bg-white/12 px-3 py-5 text-center font-mono font-semibold text-base/5 text-white">
          {midTruncate(internalAddress, 18)}
        </div>

        <div className="dark grid grid-cols-2 gap-2">
          <Copy text={internalAddress}>
            {(copied) => (
              <Button size="lg" variant="primary">
                {copied ? "Copied!" : "Copy"}
              </Button>
            )}
          </Copy>
          <Button size="lg" variant="primary" onClick={handleShareAddress}>
            {shareCopied ? "Link copied!" : "Share"}
          </Button>
        </div>
      </div>

      <ul className="mt-5 space-y-3">
        <li className="flex items-start gap-3">
          <div className="flex h-5 shrink-0 items-center justify-center">
            <CheckCircleIcon className="size-4 text-gray-500 dark:text-gray-400" />
          </div>
          <span className="font-medium text-gray-500 text-sm dark:text-gray-400">
            This is your near.com account address.
          </span>
        </li>
        <li className="flex items-start gap-3">
          <div className="flex h-5 shrink-0 items-center justify-center">
            <ExclamationCircleIcon className="size-4 text-yellow-400 dark:text-yellow-300" />
          </div>
          <span className="font-medium text-gray-500 text-sm dark:text-gray-400">
            Only for{" "}
            <span className="font-semibold text-gray-900 dark:text-white">
              transfers between near.com accounts
            </span>
            .
          </span>
        </li>
        <li className="flex items-start gap-3">
          <div className="flex h-5 shrink-0 items-center justify-center">
            <ExclamationCircleIcon className="size-4 text-yellow-400 dark:text-yellow-300" />
          </div>
          <span className="font-medium text-gray-500 text-sm dark:text-gray-400">
            Funds sent from external wallets{" "}
            <span className="font-semibold text-gray-900 dark:text-white">
              will be lost
            </span>
            .
          </span>
        </li>
      </ul>
    </div>
  )
}

export default InternalAddressView
